import { motion } from "framer-motion";
import { MessageCircle, Mail, Instagram } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHANNELS = [
  {
    key: "whatsapp",
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+974 7738 0447",
    href: "https://wa.me/97477380447",
  },
  {
    key: "email",
    icon: Mail,
    label: "Email",
    value: "contact@triodocz.com",
    href: "mailto:contact@triodocz.com",
  },
  {
    key: "instagram",
    icon: Instagram,
    label: "Instagram",
    value: "@triodocz",
    href: "https://instagram.com/triodocz",
  },
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Maintenance = () => (
  <div
    className="min-h-screen bg-[#FAFAFA] text-slate-900 antialiased flex flex-col"
    data-testid="maintenance-page"
  >
    <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-black/5">
      <p className="font-display text-2xl font-semibold tracking-tight text-slate-900">
        TrioDocz<span className="text-slate-400">.</span>
      </p>
      <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-400">
        The Visa Documentation People
      </p>
    </header>

    <main className="flex-1 flex items-center px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="h-px w-12 bg-slate-900" />
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">
            We'll be back soon
          </span>
        </motion.div>

        <h1
          data-testid="maintenance-headline"
          className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-slate-900"
        >
          <MaskedLine delay={0.15}>We're upgrading</MaskedLine>
          <MaskedLine delay={0.28}>
            our <em className="italic font-medium text-slate-500">site.</em>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
          className="mt-9 max-w-lg text-base text-slate-600 leading-relaxed"
        >
          TrioDocz is getting a fresh new look. We're working behind the
          scenes and will be back soon. Need visa paperwork handled in the
          meantime? We're still one message away.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-12 flex flex-wrap gap-4"
        >
          {CHANNELS.map((c) => (
            <a
              key={c.key}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`maintenance-${c.key}`}
              className="group flex items-center gap-3 bg-white border border-slate-200 pl-4 pr-6 py-3.5 rounded-full hover:border-slate-900 transition-colors duration-300"
            >
              <span className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-full transition-colors duration-300 group-hover:border-slate-900 group-hover:bg-slate-900">
                <c.icon className="w-4 h-4 text-slate-700 transition-colors duration-300 group-hover:text-white" />
              </span>
              <span className="text-left">
                <span className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400">
                  {c.label}
                </span>
                <span className="block text-sm font-semibold text-slate-900">{c.value}</span>
              </span>
            </a>
          ))}
        </motion.div>
      </div>
    </main>

    <footer className="px-6 md:px-12 py-8 border-t border-black/5 flex items-center justify-between">
      <p className="text-xs text-slate-400">© 2026 TrioDocz · triodocz.com</p>
      <a
        href="/"
        data-testid="maintenance-preview-link"
        className="text-xs text-slate-300 hover:text-slate-500 transition-colors duration-300"
      >
        View live site →
      </a>
    </footer>
  </div>
);
